import React, { useEffect, useState, useRef } from 'react';
import {
  PopupWindow,
  Button,
  ValidationStyle,
  InputList,
  MessagePopupCore,
  validationEngineFromInputInformationList,
  isEmpty,
} from '@utahdts/dts-react-common';
import PropTypes from 'prop-types';
import useRefLazy from '../../misc/hooks/useRefLazy';
import userInputs from '../inputs/userInputs';
import Icon from '../../misc/Icon';
import Role from '../Role';
import roles from '../../app/roles/roles';
import security from '../../app/roles/security';

const propTypes = {
  user: PropTypes.object.isRequired,

  isNew: PropTypes.bool.isRequired,

  orgs: PropTypes.array,

  onClose: PropTypes.func.isRequired,

  onSave: PropTypes.func.isRequired,
};
const defaultProps = {
  orgs: [],
};

const User = ({ user, orgs, isNew, onClose, onSave }) => {
  const [error, setError] = useState(undefined);
  const [isBusy, setBusy] = useState(false);

  // Email and org-role management are Administrator-only. Org Administrators
  // (and anyone who is not Administrator/Developer) only see and edit the
  // user's name and phone; the email field and the entire role section are
  // hidden from them.
  const canManageUsers = security.hasAnyRole([roles.dev, roles.admin]);

  const [inputValues, setInputValues] = useState(() =>
    Object.assign(
      {
        id: null,
        name: null,
        email: null,
        phone: null,
        orgs: [],
        roles: [],
      },
      user,
    ),
  );
  const inputValuesRef = useRef();
  inputValuesRef.current = inputValues;

  const [validationEngineStyle, setValidationEngineStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const validationEngineRef = useRef(undefined);

  const inputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setInputValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      isNew,
      true,
      // Email is editable only when CREATING a user. On edit it is shown as an
      // immutable, read-only field (with change-procedure help) rendered outside
      // the InputList, so it is excluded from the InputList when editing.
      canManageUsers && isNew,
    ),
  );

  const [validationEngine, setValidationEngine] = useState(undefined);
  validationEngineRef.current = validationEngine;
  if (validationEngine) {
    validationEngine.validationStyle = validationEngineStyle;
  }

  const submit = () => {
    setValidationEngineStyle(ValidationStyle.ALWAYS);
    if (
      validationEngineRef.current &&
      validationEngineRef.current.isValid(inputValuesRef.current) &&
      // Role validation only applies when the editor can manage roles; Org
      // Administrators edit only name/phone and leave the roles untouched.
      (!canManageUsers || checkRoles())
    ) {
      setBusy(true);
      setError(undefined);
      onSave(inputValues, isNew).catch(() => setBusy(false));
    }
  };

  const checkRoles = () => {
    setError(undefined);
    let check = true;
    let cleanedUpRoles = [].concat(inputValues.roles);
    cleanedUpRoles.forEach((role) => {
      if (
        (isEmpty(role.role) && isEmpty(role.org)) ||
        (!isEmpty(role.role) &&
          ![roles.admin, roles.dev].includes(role.role) &&
          isEmpty(role.org)) ||
        (!isEmpty(role.org) && isEmpty(role.role))
      ) {
        role.error =
          'A valid role requires a value and a company assigned to it.';
        check = false;
      } else {
        role.error = undefined;
      }
    });
    setInputValues((oldValues) => ({ ...oldValues, roles: cleanedUpRoles }));

    // A user may have ALL roles removed (Administrators/Org Administrators can
    // strip every role). Only require a valid role when at least one role row is
    // present — an empty role list is valid; a partially-filled row still errors.
    if (
      cleanedUpRoles.length > 0 &&
      cleanedUpRoles.filter((r) => isEmpty(r.error)).length === 0
    ) {
      setError('A valid role is required.');
      check = false;
    }

    return check;
  };

  const onChange = (field, value) =>
    setInputValues((oldValues) => ({ ...oldValues, [field]: value }));

  const updateRole = (field, value, index) => {
    let items = [...inputValues.roles];
    let item = { ...items[index] };
    item[field] = value;
    items[index] = item;
    setInputValues((oldValues) => ({ ...oldValues, roles: items }));
  };

  const deleteRole = (index) => {
    let allRoles = inputValues.roles.concat([]);
    allRoles.splice(index, 1);
    setInputValues((oldValues) => ({ ...oldValues, roles: allRoles }));
  };

  useEffect(() => {
    setValidationEngine(
      validationEngineFromInputInformationList(inputs.current),
    );
  }, [inputs.current]);

  // Email is the immutable business key of a pharmacy-user. When editing, the
  // field is read-only and a question-mark help icon next to the label opens a
  // modal explaining the supported way to change an email — delete the user,
  // recreate with the correct email, then reassign their roles.
  const EMAIL_CHANGE_MESSAGE =
    'A user\u2019s email is their permanent identifier and cannot be edited. ' +
    'To change it: 1) Delete this user, 2) Recreate the user with the correct ' +
    'email, then 3) Reassign their roles.';
  const showEmailHelp = () =>
    MessagePopupCore.addMessage({
      title: 'Changing a user\u2019s email',
      message: EMAIL_CHANGE_MESSAGE,
    });

  return (
    <PopupWindow
      className="popup-window--large"
      onCloseButtonCallback={onClose}
      footerChildren={[
        <Button
          label="Close"
          onClick={onClose}
          key="close-button"
          className="button--black"
        />,
        <Button
          label="Save"
          busy={isBusy}
          onClick={submit}
          key="save-button"
        />,
      ]}
    >
      <h3>{inputValues.id ? 'Edit User' : 'Add New User'}</h3>
      <InputList
        inputs={Object.values(inputs.current)}
        data={inputValues}
        validationEngine={validationEngine}
        className="stack-form flex flex-wrap half-form"
      />
      {canManageUsers && !isNew && (
        <div
          className="stack-form mb-spacing email-immutable"
          data-testid="email-immutable"
        >
          <div className="input__label">
            Email{' '}
            <button
              type="button"
              className="email-immutable__help-icon"
              data-testid="email-help-icon"
              aria-label="Why can’t the email be changed?"
              title="Why can’t the email be changed?"
              onClick={showEmailHelp}
            >
              ?
            </button>
          </div>
          <div className="readonly-value">{inputValues.email}</div>
        </div>
      )}
      {canManageUsers && (
        <>
          <Button
            label="Add Role"
            className="mr-spacing flex align-items-center button--short mb-spacing"
            onClick={() =>
              onChange(
                'roles',
                inputValues.roles.concat([
                  {
                    org: null,
                    role: null,
                    primaryContact: null,
                    alternativeContact: null,
                  },
                ]),
              )
            }
            children={
              <span className="icon--round ml-spacing">{Icon.iconPlus()}</span>
            }
            childrenPostion={Button.CHILDREN_POSITION.AFTER}
          />
          {error && <div className="form-error mb-spacing">{error}</div>}
          {inputValues.roles?.map((role, index) => (
            <Role
              role={role}
              orgs={orgs}
              index={index}
              deleteRole={deleteRole}
              onChange={updateRole}
              key={'role-user-' + index}
            />
          ))}
        </>
      )}
    </PopupWindow>
  );
};

User.propTypes = propTypes;
User.defaultProps = defaultProps;

export default User;
